// AR Report inline glossary system.
//
// `useARGlossary()` fetches the operator-facing definitions once per session
// (cached via React Query) and returns a typed reader function for any term
// in the report. `<Hint>` is a hover-tooltip (and click-to-pin popover) that
// shows the full definition next to any badge / metric / column header.
//
// The goal: an operator new to AR billing can hover any element and learn
// exactly what it means, what triggers it, and how it should affect their
// recovery decisions — without leaving the page.

import { useQuery } from '@tanstack/react-query'
import { useState, useRef, useEffect, type ReactNode } from 'react'
import { api, type ARGlossary, type ARGlossaryEntry } from '../api/client'

export function useARGlossary(): ARGlossary | null {
  const q = useQuery({
    queryKey: ['ar-report', 'glossary'],
    queryFn: () => api.get<ARGlossary>('/api/ar-report/glossary'),
    staleTime: 24 * 60 * 60_000,
    gcTime: 24 * 60 * 60_000,
  })
  return q.data ?? null
}

type GlossarySection = keyof Omit<ARGlossary, 'schedule'>

export function lookupGlossary(
  glossary: ARGlossary | null,
  section: GlossarySection,
  key: string | null | undefined,
): ARGlossaryEntry | null {
  if (!glossary || !key) return null
  return glossary[section]?.[key] ?? null
}

// ----------------------------------------------------------------------
// <Hint> — hover-or-click popover that shows a definition on demand.
//
// Usage:
//   <Hint title="First-attempt success" detail="...">First-attempt success</Hint>
//   <Hint section="failure_categories" termKey="invalid_card_closed">
//     <Badge>...</Badge>
//   </Hint>
// ----------------------------------------------------------------------

type HintProps = {
  children: ReactNode
  // Direct definition strings (when not using glossary lookup)
  title?: string
  short?: string
  detail?: string
  recovery?: string
  // Or pull from the glossary by section + key
  section?: GlossarySection
  termKey?: string | null
  // Click target for "learn more" link, e.g. an anchor in a longer reference page
  anchor?: string
  // Customise the underline color of the trigger
  underline?: 'amber' | 'cyan' | 'paper' | 'none'
}

export function Hint(props: HintProps) {
  const glossary = useARGlossary()
  const entry = props.section && props.termKey
    ? lookupGlossary(glossary, props.section, props.termKey)
    : null

  const title = props.title ?? entry?.label
  const short = props.short ?? entry?.short
  const detail = props.detail ?? entry?.detail
  const recovery = props.recovery ?? entry?.recovery

  const [open, setOpen] = useState(false)
  const [pinned, setPinned] = useState(false)
  const wrapperRef = useRef<HTMLSpanElement | null>(null)

  // Click outside to dismiss when pinned
  useEffect(() => {
    if (!pinned) return
    const onDoc = (e: MouseEvent) => {
      if (!wrapperRef.current) return
      if (!wrapperRef.current.contains(e.target as Node)) {
        setPinned(false)
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [pinned])

  if (!title && !short && !detail) {
    // No definition available — render children passthrough
    return <>{props.children}</>
  }

  const showing = open || pinned
  const underlineCls =
    props.underline === 'none' ? '' :
    props.underline === 'amber' ? 'border-b border-dotted border-amber-glow/50' :
    props.underline === 'cyan' ? 'border-b border-dotted border-radar-cyan/50' :
    'border-b border-dotted border-paper-trace/50'

  return (
    <span ref={wrapperRef} className="relative inline-block">
      <span
        className={`cursor-help inline-flex items-baseline gap-1 ${underlineCls}`}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => !pinned && setOpen(false)}
        onClick={(e) => {
          e.stopPropagation()
          setPinned(!pinned)
          setOpen(true)
        }}
      >
        {props.children}
      </span>
      {showing && (
        <span
          role="tooltip"
          className="absolute z-50 left-0 top-full mt-1 w-[340px] bg-ink-900 border border-amber-glow/60 shadow-xl p-3 text-left text-paper text-[12px] leading-snug font-sans normal-case"
          onClick={(e) => e.stopPropagation()}
        >
          {title && (
            <div className="font-mono tracking-label text-amber-glow text-[10px] uppercase mb-1">
              {title}
            </div>
          )}
          {short && (
            <div className="text-paper text-[13px] mb-2 font-medium">{short}</div>
          )}
          {detail && (
            <div className="text-paper-dim text-[12px] mb-2 leading-[1.5]">{detail}</div>
          )}
          {recovery && (
            <div className="text-radar-cyan text-[11px] border-t border-ink-500 pt-2 mt-2">
              <span className="text-paper-faint">Recovery:</span> {recovery}
            </div>
          )}
          {pinned && (
            <div className="text-[10px] text-paper-faint mt-2 font-mono">
              Click outside to close
            </div>
          )}
        </span>
      )}
    </span>
  )
}

// ----------------------------------------------------------------------
// Convenience badges that already have hover tooltips wired in
// ----------------------------------------------------------------------

const COLOR_CLASSES: Record<string, string> = {
  green: 'bg-radar-green/15 text-radar-green border-radar-green/40',
  amber: 'bg-amber-glow/15 text-amber-glow border-amber-glow/40',
  red: 'bg-radar-red/15 text-radar-red border-radar-red/40',
  cyan: 'bg-radar-cyan/15 text-radar-cyan border-radar-cyan/40',
  gray: 'bg-paper-trace/10 text-paper-dim border-paper-trace/40',
}

export function StatusBadge({ status }: { status: string | null }) {
  const glossary = useARGlossary()
  if (!status) return <span className="font-mono text-[10px] text-paper-trace">—</span>
  const entry = lookupGlossary(glossary, 'statuses', status)
  const color = entry?.color ?? 'gray'
  return (
    <Hint section="statuses" termKey={status} underline="none">
      <span className={`inline-block px-2 py-0.5 border font-mono text-[10px] uppercase ${COLOR_CLASSES[color] || COLOR_CLASSES.gray}`}>
        {status}
      </span>
    </Hint>
  )
}

export function RetryPathBadge({ path }: { path: 'fast' | 'standard' | null }) {
  if (!path) return null
  const glossary = useARGlossary()
  const entry = lookupGlossary(glossary, 'retry_paths', path)
  const color = path === 'fast' ? 'cyan' : 'gray'
  const label = path === 'fast' ? '⚡ fast +22h' : 'standard'
  return (
    <Hint section="retry_paths" termKey={path} underline="none">
      <span className={`inline-block px-1.5 py-0.5 border font-mono text-[9px] uppercase ${COLOR_CLASSES[color]}`} title={entry?.short}>
        {label}
      </span>
    </Hint>
  )
}

export function FailureCategoryBadge({ category }: { category: string | null }) {
  if (!category) return null
  const glossary = useARGlossary()
  const entry = lookupGlossary(glossary, 'failure_categories', category)
  return (
    <Hint section="failure_categories" termKey={category} underline="none">
      <span className="inline-block px-1.5 py-0.5 border border-paper-trace/30 bg-ink-700/40 font-mono text-[10px] text-paper-dim">
        {entry?.label ?? category}
      </span>
    </Hint>
  )
}

export function CardHealthBadge({ health }: { health: string | null }) {
  if (!health) return null
  const glossary = useARGlossary()
  const entry = lookupGlossary(glossary, 'card_health', health)
  const color = entry?.color ?? 'gray'
  return (
    <Hint section="card_health" termKey={health} underline="none">
      <span className={`inline-block px-1.5 py-0.5 border font-mono text-[9px] uppercase whitespace-nowrap ${COLOR_CLASSES[color] || COLOR_CLASSES.gray}`}>
        {health.replace(/_/g, ' ')}
      </span>
    </Hint>
  )
}

export function LifecycleBadge({ state }: { state: string | null }) {
  if (!state) return null
  const glossary = useARGlossary()
  const entry = lookupGlossary(glossary, 'lifecycle_states', state)
  return (
    <Hint section="lifecycle_states" termKey={state} underline="none">
      <span className="inline-block px-2 py-0.5 border border-amber-glow/40 bg-amber-glow/10 font-mono text-[10px] uppercase text-amber-glow">
        {entry?.label ?? state}
      </span>
    </Hint>
  )
}
